import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { api } from '../api/client.js';
import { Alert } from './ui.jsx';

/**
 * Two-step verification, shown on the account page for superadmins.
 *
 * Three states: off, mid-enrolment (secret issued, waiting for a code
 * that proves the app has it), and on. Recovery codes appear exactly
 * once, right after enrolling - the server keeps only hashes, so there
 * is no second chance to read them.
 */
export default function TwoStepCard({ onChange }) {
  const [status, setStatus] = useState(null);
  const [setup, setSetup] = useState(null);      // { secret, otpauthUri }
  const [qr, setQr] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [codes, setCodes] = useState(null);      // shown once, after enabling
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () =>
    api.totpStatus().then(setStatus).catch((err) => setError(err.message));

  useEffect(() => {
    load();
  }, []);

  // The QR is drawn in the browser, so the secret never travels as an
  // image through anything that might cache it.
  useEffect(() => {
    if (!setup?.otpauthUri) return setQr('');
    QRCode.toDataURL(setup.otpauthUri, { width: 220, margin: 1 })
      .then(setQr)
      .catch(() => setQr(''));
  }, [setup]);

  const begin = async () => {
    setError('');
    setBusy(true);
    try {
      setSetup(await api.totpSetup());
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const confirm = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const { recoveryCodes } = await api.totpEnable(code.trim());
      setCodes(recoveryCodes);
      setSetup(null);
      setCode('');
      await load();
      onChange?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const turnOff = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await api.totpDisable(password);
      setPassword('');
      setCodes(null);
      await load();
      onChange?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!status) return null;

  return (
    <section className="card">
      <h2>🔐 Two-step verification</h2>
      <p className="muted small" style={{ marginBottom: '1.2rem' }}>
        A superadmin can see and change everything on CyberCub, so this account can ask for a
        6-digit code from your phone as well as your password. Children and teachers are not
        affected.
      </p>

      {error && <Alert kind="bad" onDismiss={() => setError('')}>{error}</Alert>}

      {/* ---------- recovery codes, shown once ---------- */}
      {codes && (
        <Alert kind="warn">
          <strong>Save these recovery codes now.</strong> Each one signs you in once if you lose
          your phone. They are not shown again.
          <div className="recovery-codes">
            {codes.map((c) => <code key={c}>{c}</code>)}
          </div>
          <button
            type="button"
            className="btn btn-sm btn-ghost"
            onClick={() => navigator.clipboard?.writeText(codes.join('\n'))}
          >
            Copy all
          </button>
        </Alert>
      )}

      {/* ---------- on ---------- */}
      {status.enabled && !setup && (
        <>
          <div className="row gap-sm" style={{ marginBottom: '1rem' }}>
            <span className="pill pill-good">✓ On</span>
            <span className="small muted">
              {status.recoveryCodesLeft} recovery code
              {status.recoveryCodesLeft === 1 ? '' : 's'} left
            </span>
          </div>

          {status.recoveryCodesLeft <= 2 && (
            <Alert kind="warn">
              You are nearly out of recovery codes. Turn two-step verification off and on again to
              get a fresh set.
            </Alert>
          )}

          <form onSubmit={turnOff}>
            <div className="field">
              <label htmlFor="totp-off">Your password, to turn it off</label>
              <input
                id="totp-off"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <button type="submit" className="btn btn-ghost danger" disabled={busy || !password}>
              {busy ? 'Working…' : 'Turn off two-step verification'}
            </button>
          </form>
        </>
      )}

      {/* ---------- off ---------- */}
      {!status.enabled && !setup && (
        <>
          <div className="row gap-sm" style={{ marginBottom: '1rem' }}>
            <span className="pill pill-warn">Off</span>
          </div>
          <button type="button" className="btn" onClick={begin} disabled={busy}>
            {busy ? 'Working…' : 'Turn on two-step verification'}
          </button>
        </>
      )}

      {/* ---------- mid-enrolment ---------- */}
      {setup && (
        <>
          <ol className="totp-steps">
            <li>
              Open an authenticator app — Google Authenticator, Authy, or the one built into your
              password manager.
            </li>
            <li>Scan this code, or type the key in by hand.</li>
            <li>Enter the 6-digit code it shows, to prove it worked.</li>
          </ol>

          <div className="totp-enrol">
            {qr ? (
              <img src={qr} alt="QR code for your authenticator app" width={220} height={220} />
            ) : (
              <div className="totp-qr-fallback small muted">
                Could not draw the QR code — type the key in instead.
              </div>
            )}

            <div>
              <span className="small muted">Setup key</span>
              <code className="totp-secret">{setup.secret}</code>
              <button
                type="button"
                className="btn btn-sm btn-ghost"
                onClick={() => navigator.clipboard?.writeText(setup.secret)}
              >
                Copy key
              </button>
            </div>
          </div>

          <form onSubmit={confirm}>
            <div className="field">
              <label htmlFor="totp-code">6-digit code from the app</label>
              <input
                id="totp-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="123456"
                className="totp-input"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                maxLength={6}
                required
                autoFocus
              />
            </div>
            <div className="row gap-sm">
              <button type="submit" className="btn" disabled={busy || code.trim().length < 6}>
                {busy ? 'Checking…' : 'Turn it on'}
              </button>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => { setSetup(null); setCode(''); setError(''); }}
              >
                Cancel
              </button>
            </div>
          </form>
        </>
      )}
    </section>
  );
}
